import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Autocomplete,
  Chip,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import axios from "axios";

interface EventLibraryItem {
  id_event_library: number;
  event_code: string;
  event_type: string;
  event_description: string;
}

interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  onMemberAdded: () => void;
  onShowSnackbar: (
    message: string,
    severity: "success" | "error",
    memberCodename?: string,
    memberId?: string
  ) => void;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  open,
  onClose,
  onMemberAdded,
  onShowSnackbar,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [address, setAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [eventLibrary, setEventLibrary] = useState<EventLibraryItem[]>([]);
  const [selectedDiagnoses, setSelectedDiagnoses] = useState<
    EventLibraryItem[]
  >([]);
  const [selectedMedications, setSelectedMedications] = useState<
    EventLibraryItem[]
  >([]);
  const [selectedTreatments, setSelectedTreatments] = useState<
    EventLibraryItem[]
  >([]);
  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (open) {
      fetchEventLibrary();
    }
  }, [open]);

  const fetchEventLibrary = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/event-library`
      );
      setEventLibrary(response.data);
    } catch (error) {
      console.error("Error fetching event library:", error);
      onShowSnackbar("Error fetching event library", "error");
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!dob) newErrors.dob = "Date of birth is required";
    if (!gender) newErrors.gender = "Gender is required";
    if (!email) newErrors.email = "Email is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }
    if (phone && !/^\+?[\d\s-()]{10,}$/.test(phone)) {
      newErrors.phone = "Invalid phone number format";
    }
    if (zipCode && !/^\d{5}(-\d{4})?$/.test(zipCode)) {
      newErrors.zipCode = "Invalid ZIP code format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setDob("");
    setGender("");
    setAddress("");
    setZipCode("");
    setPhone("");
    setEmail("");
    setSelectedDiagnoses([]);
    setSelectedMedications([]);
    setSelectedTreatments([]);
    setActiveTab(0);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const healthRecords = [
        ...selectedDiagnoses.map((event) => ({
          medical_event: event.event_description,
          description: event.event_description,
          event_code: event.event_code,
          event_type: "Diagnosis",
          comment: "",
        })),
        ...selectedMedications.map((event) => ({
          medical_event: event.event_description,
          description: event.event_description,
          event_code: event.event_code,
          event_type: "Medication",
          comment: "",
        })),
        ...selectedTreatments.map((event) => ({
          medical_event: event.event_description,
          description: event.event_description,
          event_code: event.event_code,
          event_type: "Treatment",
          comment: "",
        })),
      ];

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/members`,
        {
          first_name: firstName,
          last_name: lastName,
          dob,
          gender,
          address,
          zip_code: zipCode,
          phone,
          email,
          healthRecords,
        }
      );

      onShowSnackbar(
        `Member added successfully! Member ID: ${response.data.codename}`,
        "success"
      );
      onMemberAdded();
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error adding member:", error);
      onShowSnackbar("Error adding member. Please try again.", "error");
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getFilteredEvents = () => {
    return eventLibrary.filter(
      (event) =>
        event.event_type === ["Diagnosis", "Medication", "Treatment"][activeTab]
    );
  };

  const getCurrentSelections = () => {
    switch (activeTab) {
      case 0:
        return selectedDiagnoses;
      case 1:
        return selectedMedications;
      case 2:
        return selectedTreatments;
      default:
        return [];
    }
  };

  const handleSelectionChange = (newValue: EventLibraryItem[]) => {
    switch (activeTab) {
      case 0:
        setSelectedDiagnoses(newValue);
        break;
      case 1:
        setSelectedMedications(newValue);
        break;
      case 2:
        setSelectedTreatments(newValue);
        break;
    }
  };

  const renderSelectedItemsPreview = () => {
    const allSelections = [
      { items: selectedDiagnoses, label: "Diagnoses" },
      { items: selectedMedications, label: "Medications" },
      { items: selectedTreatments, label: "Treatments" },
    ].filter((group) => group.items.length > 0);

    if (allSelections.length === 0) return null;

    return (
      <Paper sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5" }}>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Health Records to be Added:
        </Typography>
        {allSelections.map((group, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <Typography variant="caption" color="textSecondary">
              {group.label}:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {group.items.map((item) => (
                <Chip
                  key={item.id_event_library}
                  label={item.event_description}
                  size="small"
                  sx={{ backgroundColor: "white" }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Paper>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Add New Participant
        </Typography>
        <TextField
          label="First name *"
          fullWidth
          margin="normal"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          error={!!errors.firstName}
          helperText={errors.firstName}
          required
        />
        <TextField
          label="Last name *"
          fullWidth
          margin="normal"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          error={!!errors.lastName}
          helperText={errors.lastName}
          required
        />
        <TextField
          label="Date of Birth *"
          type="date"
          fullWidth
          margin="normal"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!errors.dob}
          helperText={errors.dob}
          required
        />
        <FormControl fullWidth margin="normal" error={!!errors.gender} required>
          <InputLabel>Gender *</InputLabel>
          <Select value={gender} onChange={(e) => setGender(e.target.value)}>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
          {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
        </FormControl>
        <TextField
          label="Address"
          fullWidth
          margin="normal"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />
        <TextField
          label="ZIP Code"
          fullWidth
          margin="normal"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          error={!!errors.zipCode}
          helperText={errors.zipCode}
        />
        <TextField
          label="Phone Number"
          fullWidth
          margin="normal"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={!!errors.phone}
          helperText={errors.phone}
        />
        <TextField
          label="Email *"
          type="email"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          required
        />
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Health Records
        </Typography>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Diagnosis" />
          <Tab label="Medication" />
          <Tab label="Treatment" />
        </Tabs>
        <Autocomplete
          multiple
          options={getFilteredEvents()}
          getOptionLabel={(option) => option.event_description}
          value={getCurrentSelections()}
          onChange={(event, newValue) => {
            handleSelectionChange(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={`Select ${
                ["Diagnosis", "Medication", "Treatment"][activeTab]
              }`}
              placeholder={`Search for ${[
                "diagnosis",
                "medication",
                "treatment",
              ][activeTab].toLowerCase()}`}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => <></>)
          }
        />

        {renderSelectedItemsPreview()}

        <FormHelperText>* Required fields</FormHelperText>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmit}
            sx={{ bgcolor: "#5958e1", "&:hover": { bgcolor: "#4a49b3" } }}
          >
            Add
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddMemberModal;
